import React, { useState } from "react";
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Box,
  Typography,
  TablePagination,
  Chip,
} from "@mui/material";

// <-- 1. Add `showPagination` prop with a default value of true
const OrdersDetails = ({ orders, showPagination = true }) => {
  const [selectedDate, setSelectedDate] = useState("");
  const [page, setPage] = useState(0); // pagination page
  const [rowsPerPage, setRowsPerPage] = useState(10); // rows per page

  // filter orders by selected date
  const filteredOrders = orders.filter((order) => {
    if (!selectedDate) return true;

    const d = new Date(order.startTime);
    const orderDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;

    return orderDate === selectedDate;
  });

  // format date/time helper
  const formatDateTime = (dateString) => {
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year}, ${hours}:${minutes}`;
  };

  // handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // reset page when changing rows
  };

  // <-- 2. Conditionally slice orders or show all filtered orders
  const ordersToDisplay = showPagination
    ? filteredOrders.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      )
    : filteredOrders;

  return (
    <Container maxWidth={false} sx={{ width: "100%", mt: 5 }}>
      {/* Header with filter */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Orders
        </Typography>
      </Box>

      <Paper>
        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
            "@media (max-width:600px)": {
              "& td, & th": { padding: "6px", fontSize: "12px" },
            },
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>Order No</b>
                </TableCell>
                <TableCell>
                  <b>Type</b>
                </TableCell>
                <TableCell>
                  <b>Order Status</b>
                </TableCell>
                <TableCell>
                  <b>Amount</b>
                </TableCell>
                <TableCell>
                  <b>Payment Status</b>
                </TableCell>
                <TableCell>
                  <b>Date and Time</b>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* <-- 3. Map over `ordersToDisplay` instead of `paginatedOrders` */}
              {ordersToDisplay.length > 0 ? (
                ordersToDisplay.map((order, index) => (
                  <TableRow key={index}>
                    <TableCell>{order.orderNo}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          order.type === 0
                            ? "In-Store"
                            : order.type === 1
                            ? "Kiosk"
                            : order.type === 2
                            ? "Dine-in"
                            : order.type === 3
                            ? "Online"
                            : "Unknown"
                        }
                        color={
                          order.type === 0
                            ? "success" // purple/gray
                            : order.type === 1
                            ? "primary" // blue
                            : order.type === 2
                            ? "warning" // light blue
                            : order.type === 3
                            ? "default" // green
                            : "default"
                        }
                        variant="outlined"
                        sx={{ marginLeft: "5px" }}
                      />
                    </TableCell>
                    <TableCell>
                      {order.oStatus === 0 && "Some part is missing(current)"}
                      {order.oStatus === 1 && "Active (current)"}
                      {order.oStatus === 2 && "Completed"}
                      {order.oStatus === 5 && "Cancelled"}
                    </TableCell>
                    <TableCell>${order.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          order.payment && order.payment.length > 0
                            ? "Successful"
                            : "In Progress"
                        }
                        color={
                          order.payment && order.payment.length > 0
                            ? "success" // ✅ green
                            : "primary" // ✅ blue
                        }
                        sx={{ marginLeft: "5px" }}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDateTime(order.startTime)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No orders found for this date.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* <-- 4. Conditionally render the pagination controls */}
        {showPagination && (
          <TablePagination
            component="div"
            count={filteredOrders.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        )}
      </Paper>
    </Container>
  );
};

export default OrdersDetails;